import { ReactNode, useEffect, useState } from 'react'
import { useUserState } from '../../providers/UserProvider'
import { type User } from '../../utils/User'
import { stripeRequest } from '../../lib/stripe'
import { identify, peopleSet, trackEvent } from '../../lib/analytics'
import { useFetchUserDefault } from '../../hooks/useFetchUserDefault'
import { setUserDefault } from '../../lib/cloudkit/userDefaults'
import { SubscriptionVerificationContext } from './context'

function trackTrialBannerClicked() {
  trackEvent('WEB - Trial Banner Clicked')
}

type SubscriptionVerificationProviderProps = {
  children: ReactNode
}

export function SubscriptionVerificationProvider({
  children,
}: SubscriptionVerificationProviderProps) {
  const [error, setError] = useState<string | undefined>(undefined)
  const [isTrialLoading, setIsTrialLoading] = useState<boolean>(true)
  const user = useUserState()
  const [trialEndDate, setTrialEndDate] = useState<string | undefined>(
    undefined
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isGuest, setIsGuest] = useState(false)
  const [isTrialRunning, setIsTrialRunning] = useState<string | undefined>(
    undefined
  )
  const { data: revenueCatUserId } = useFetchUserDefault(
    'revenuecat_id',
    'web-app-subscription-rc-id'
  )
  const [stripeCustomerId, setStripeCustomerId] = useState<string | undefined>(
    undefined
  )
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState<
    string | undefined
  >(undefined)
  const [mixpanelAcctCreatedEvnt, setMixpanelAcctCreatedEvnt] = useState<
    boolean | undefined
  >(false)

  async function manageTrial(
    currentUser: User,
    currentRevenueCatUserId: string
  ) {
    try {
      setIsTrialLoading(true)
      const response = await stripeRequest<object>('/trial.php', {
        user: currentUser,
        revenueCatUserId: currentRevenueCatUserId,
      })
      return response
    } catch (requestError) {
      setError(String(requestError))
      return { error: String(requestError) }
    } finally {
      setIsTrialLoading(false)
    }
  }

  async function manageSupabaseTrial(userId: string) {
    try {
      // setIsTrialLoading(true)
      const response = await stripeRequest<object>('/supabase-trial.php', {
        user_id: userId,
      })

      setIsTrialLoading(false)
      return response
    } catch (requestError) {
      setError(String(requestError))
      return { error: String(requestError) }
    } finally {
      setIsTrialLoading(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return

      if (user.cloudKitUserId) {
        if (revenueCatUserId === undefined) return

        // Check through RevenueCat, if the subscription exists,
        // console.log('REVENUECAT ID', revenueCatUserId)
        const trialResponse = await manageTrial(user, revenueCatUserId)
        // console.log(
        //   '[useSubscriptionVerification] CloudKit user response',
        //   trialResponse
        // )

        if (!revenueCatUserId && trialResponse.revenuecat_id) {
          try {
            await setUserDefault(
              'web-app-subscription-rc-id',
              trialResponse.revenuecat_id
            )
          } catch (requestError) {
            // console.error('Failed to save revenuecat_id:', requestError)
            // TODO: implement error reporting and toast
          }
        }
        if (!mixpanelAcctCreatedEvnt && trialResponse.trial_start_date) {
          const trialStartDate = new Date(trialResponse.trial_start_date)
          const today = new Date()
          trialStartDate.setHours(0, 0, 0, 0), today.setHours(0, 0, 0, 0)
          const isToday = trialStartDate.getTime() === today.getTime()
          if (isToday) trackEvent('WEB - Account Created')
          setMixpanelAcctCreatedEvnt(true)
          // console.log('[batch]', isToday, trialStartDate)
        }

        setIsSubscribed(trialResponse.subscribed ?? false)

        if (!trialResponse.subscribed) {
          setTrialEndDate(
            trialResponse.trial_end_date
              ? trialResponse.trial_end_date.toString()
              : undefined
          )
          setIsTrialRunning(trialResponse.trial?.toString() ?? undefined)
        }
      } else if (user.supabaseUserId) {
        // handle subscription
        const response = await manageSupabaseTrial(user.supabaseUserId)
        // console.log('[useSubscriptionVerification] Supabase user response', res)

        setIsSubscribed(response.subscription_status ?? false)
        setStripeCustomerId(response.stripeCustomerId)
        setStripeSubscriptionId(response.stripeSubscriptionId)

        if (!mixpanelAcctCreatedEvnt && response.trial_start_date) {
          const trialStartDate = new Date(response.trial_start_date)
          const today = new Date()
          trialStartDate.setHours(0, 0, 0, 0), today.setHours(0, 0, 0, 0)
          const isToday = trialStartDate.getTime() === today.getTime()
          if (isToday) trackEvent('WEB - Account Created')
          setMixpanelAcctCreatedEvnt(true)
          // console.log('[batch]', isToday, trialStartDate)
        }

        if (response.subscription_status === false) {
          setTrialEndDate(
            response.trial_end_date
              ? response.trial_end_date.toString()
              : undefined
          )
          setIsTrialRunning(response.trial ?? undefined)
        }

        if (!response.trial || response.trial === 'expired') {
          setIsGuest(response.is_guest ?? false)
        } else {
          setIsGuest(false)
        }
      }

      if (user.supabaseUserId ?? user.cloudKitUserId) {
        identify(user.cloudKitUserId ?? user.supabaseUserId)
        peopleSet({
          supabaseUserId: user.supabaseUserId ?? '',
          cloudKitUserId: user.cloudKitUserId ?? '',
          revenueCatUserId: revenueCatUserId ?? '',
          stripeCustomerId: stripeCustomerId ?? '',
          stripeSubscriptionId: stripeSubscriptionId ?? '',
          email: user.email,
          source: 'web',
          subscribed: isSubscribed,
          trial: isTrialRunning,
          isGuest: isGuest,
        })
      }
      // setOpen(true)
    }

    void fetchData()
  }, [
    user,
    revenueCatUserId,
    mixpanelAcctCreatedEvnt,
    stripeCustomerId,
    stripeSubscriptionId,
    isSubscribed,
    isTrialRunning,
    isGuest,
  ])

  const value = {
    error,
    setError,
    isTrialLoading,
    manageTrial,
    setDialogOpen,
    dialogOpen,
    trialEndDate,
    isSubscribed,
    isGuest,
    isTrialRunning,
    stripeCustomerId,
    stripeSubscriptionId,
    trackTrialBannerClicked,
  }

  return (
    <SubscriptionVerificationContext.Provider value={value}>
      {children}
    </SubscriptionVerificationContext.Provider>
  )
}
